import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { 
  FileText, Calculator, Send, Check, X, Eye, Plus, RefreshCw,
  Building2, Users, Calendar, Euro, AlertCircle, Clock, ChevronRight
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { confirmAsync } from '@/lib/confirm';

const STATUS_COLORS = {
  borrador: 'bg-gray-100 text-gray-800',
  enviada: 'bg-blue-100 text-blue-800',
  validada: 'bg-green-100 text-green-800',
  aceptada: 'bg-green-100 text-green-800',
  rechazada: 'bg-red-100 text-red-800',
  facturada: 'bg-purple-100 text-purple-800'
};

const STATUS_LABELS = {
  borrador: 'Borrador',
  enviada: 'Enviada',
  validada: 'Aceptada',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  facturada: 'Facturada'
};

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function Settlements({ user }) {
  const [settlements, setSettlements] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('distribuidores');
  
  // Filters
  const [filterPeriodo, setFilterPeriodo] = useState('');
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  
  // Dialogs
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [settlementLines, setSettlementLines] = useState([]);
  
  // Generate form
  const [generateForm, setGenerateForm] = useState({
    partner_id: '',
    periodo: ''
  });
  
  // Validate form
  const [validateForm, setValidateForm] = useState({
    accepted: true,
    motivo_rechazo: ''
  });
  
  // Processing states
  const [processing, setProcessing] = useState(false);

  // Facturas ya cobradas que todavia no se le han liquidado a su partner.
  // No depende de las pestanas ni de los filtros: es una deuda pendiente, y
  // esconderla detras de un filtro es como no tenerla.
  const [pendientes, setPendientes] = useState(null);

  useEffect(() => {
    fetchPartners();
    fetchSettlements();
  }, [activeTab, filterPeriodo, filterPartner, filterEstado]);

  useEffect(() => {
    axiosInstance.get('/settlements/alertas/pendientes')
      .then(({ data }) => setPendientes(data))
      .catch((e) => console.error('Error cargando pendientes de liquidar:', e));
  }, []);

  const fetchPartners = async () => {
    try {
      const tipo = activeTab === 'distribuidores' ? 'distribuidor' : 'prescriptor';
      const response = await axiosInstance.get(`/partners?tipo=${tipo}`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const fetchSettlements = async () => {
    try {
      setLoading(true);
      const tipo = activeTab === 'distribuidores' ? 'distribuidor' : 'prescriptor';
      let url = `/settlements?tipo=${tipo}`;
      
      if (filterPeriodo) url += `&periodo=${filterPeriodo}`;
      if (filterPartner !== 'all') url += `&partner_id=${filterPartner}`;
      if (filterEstado !== 'all') url += `&estado=${filterEstado}`;
      
      const response = await axiosInstance.get(url);
      setSettlements(response.data);
    } catch (error) {
      console.error('Error fetching settlements:', error);
      toast.error('Error al cargar liquidaciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlementDetail = async (settlementId) => {
    try {
      const response = await axiosInstance.get(`/settlements/${settlementId}`);
      setSelectedSettlement(response.data);
      setSettlementLines(response.data.lines || []);
    } catch (error) {
      toast.error('Error al cargar detalle de liquidación');
    }
  };

  const handleGenerate = async () => {
    if (!generateForm.partner_id || !generateForm.periodo) {
      toast.error('Selecciona un partner y un periodo');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axiosInstance.post(
        `/settlements/generate?partner_id=${generateForm.partner_id}&periodo=${generateForm.periodo}`
      );
      toast.success(response.data.message || 'Liquidación generada correctamente');
      setShowGenerateDialog(false);
      setGenerateForm({ partner_id: '', periodo: '' });
      fetchSettlements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar liquidación');
    } finally {
      setProcessing(false);
    }
  };

  const handleSend = async (settlementId) => {
    if (!(await confirmAsync('¿Enviar la liquidación al partner?'))) return;
    
    try {
      const { data } = await axiosInstance.post(`/settlements/${settlementId}/send`);
      // Se dice A QUIEN se ha enviado, no solo que se ha enviado. Antes esto
      // cantaba exito sin haber mandado nada: el backend tenia un TODO donde
      // deberia estar el envio y aun asi marcaba la liquidacion como enviada.
      toast.success(data.destinatario
        ? `Liquidación enviada a ${data.destinatario}`
        : 'Liquidación enviada');
      fetchSettlements();
      if (selectedSettlement?.id === settlementId) {
        fetchSettlementDetail(settlementId);
      }
    } catch (error) {
      // Si el correo no sale, la liquidacion SIGUE en borrador y se puede
      // reintentar. Marcarla como enviada seria repetir el mismo error.
      toast.error(error.response?.data?.detail || 'Error al enviar liquidación');
    }
  };

  const handleResend = async (settlement) => {
    const destino = settlement.enviada_a ? ` a ${settlement.enviada_a}` : '';
    const ok = await confirmAsync({
      title: 'Reenviar liquidación',
      description: `Se volverá a enviar la liquidación de ${settlement.periodo}${destino}.\n\n`
        + 'El estado no cambia: sigue como está.',
      confirmText: 'Reenviar',
    });
    if (!ok) return;

    try {
      const { data } = await axiosInstance.post(`/settlements/${settlement.id}/resend`);
      toast.success(`Reenviada a ${data.destinatario}`);
      fetchSettlements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar la liquidación');
    }
  };

  const handleEmitInvoice = async (settlementId, tipo) => {
    const mensaje = tipo === 'distribuidor' 
      ? '¿Emitir factura al distribuidor?' 
      : '¿Generar factura del prescriptor y marcar para pago?';
    
    if (!(await confirmAsync(mensaje))) return;
    
    setProcessing(true);
    try {
      const endpoint = tipo === 'distribuidor' 
        ? `/settlements/${settlementId}/emit-invoice`
        : `/settlements/${settlementId}/emit-prescriptor-invoice`;
      
      const response = await axiosInstance.post(endpoint);
      toast.success(response.data.message || 'Factura emitida correctamente');
      fetchSettlements();
      if (selectedSettlement?.id === settlementId) {
        fetchSettlementDetail(settlementId);
      }
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al emitir factura');
    } finally {
      setProcessing(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedSettlement) return;
    
    setProcessing(true);
    try {
      await axiosInstance.post(
        `/settlements/${selectedSettlement.id}/validate?accepted=${validateForm.accepted}${!validateForm.accepted ? `&motivo_rechazo=${encodeURIComponent(validateForm.motivo_rechazo)}` : ''}`
      );
      toast.success(validateForm.accepted ? 'Liquidación aceptada' : 'Liquidación rechazada');
      setShowValidateDialog(false);
      setValidateForm({ accepted: true, motivo_rechazo: '' });
      fetchSettlements();
      fetchSettlementDetail(selectedSettlement.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al validar liquidación');
    } finally {
      setProcessing(false);
    }
  };

  const handleRegenerate = async (settlementId) => {
    if (!(await confirmAsync('¿Regenerar la liquidación? Se perderán los cambios actuales.'))) return;
    
    try {
      await axiosInstance.post(`/settlements/${settlementId}/regenerate`);
      toast.success('Liquidación regenerada correctamente');
      fetchSettlements();
      if (selectedSettlement?.id === settlementId) {
        fetchSettlementDetail(settlementId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al regenerar liquidación');
    }
  };

  const openDetailDialog = (settlement) => {
    setSelectedSettlement(settlement);
    fetchSettlementDetail(settlement.id);
    setShowDetailDialog(true);
  };

  const openValidateDialog = (settlement) => {
    setSelectedSettlement(settlement);
    setValidateForm({ accepted: true, motivo_rechazo: '' });
    setShowValidateDialog(true);
  };

  // Get current year and month for defaults
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // Generate period options (last 12 months)
  const periodOptions = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentYear, currentMonth - 1 - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    periodOptions.push({
      value: `${year}-${month}`,
      label: `${MONTHS[date.getMonth()]} ${year}`
    });
  }

  // Stats
  const stats = {
    total: settlements.length,
    borrador: settlements.filter(s => s.estado === 'borrador').length,
    enviada: settlements.filter(s => s.estado === 'enviada').length,
    aceptada: settlements.filter(s => s.estado === 'validada' || s.estado === 'aceptada').length,
    rechazada: settlements.filter(s => s.estado === 'rechazada').length,
    totalImporte: settlements.reduce((acc, s) => acc + (s.total || 0), 0)
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Liquidaciones</h1>
            <p className="text-gray-500">Gestión de liquidaciones mensuales de partners</p>
          </div>
          <Button onClick={() => setShowGenerateDialog(true)} className="bg-[#9C0F17]">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Liquidación
          </Button>
        </div>

        {/* Cobrado a clientes y todavia no liquidado a su partner.
            Antes esto no se podia ni preguntar: las lineas de liquidacion no
            guardaban de que factura salian, asi que una factura pagada fuera
            de un periodo ya liquidado no aparecia en ningun sitio. */}
        {pendientes?.num_partners > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-4"
               data-testid="aviso-sin-liquidar">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900">
                  Hay {pendientes.num_facturas}{' '}
                  {pendientes.num_facturas === 1 ? 'factura cobrada' : 'facturas cobradas'}
                  {' '}sin liquidar a {pendientes.num_partners}{' '}
                  {pendientes.num_partners === 1 ? 'partner' : 'partners'}
                </p>
                <p className="text-sm text-amber-800 mt-0.5">
                  Total pendiente de liquidar:{' '}
                  <strong>{pendientes.total_pendiente?.toFixed(2)}€</strong>
                </p>

                <div className="mt-3 space-y-2">
                  {pendientes.avisos.map((a) => (
                    <div key={a.partner_id}
                         className="flex items-center justify-between bg-white border border-amber-200 rounded px-3 py-2"
                         data-testid={`pendiente-${a.partner_id}`}>
                      <div className="min-w-0">
                        <span className="font-medium text-slate-900">{a.partner_name}</span>
                        <span className="text-xs text-slate-500 ml-2">({a.tipo})</span>
                        <p className="text-xs text-slate-600 mt-0.5 truncate">
                          {a.num_facturas}{' '}
                          {a.num_facturas === 1 ? 'factura' : 'facturas'}
                          {a.dias_mas_antigua !== null && (
                            <> · la más antigua lleva{' '}
                              <span className={a.dias_mas_antigua >= 60
                                ? 'text-red-700 font-semibold' : ''}>
                                {a.dias_mas_antigua} días
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="font-semibold text-slate-900">
                          {a.total_pendiente?.toFixed(2)}€
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setGenerateForm((f) => ({ ...f, partner_id: a.partner_id }));
                            setShowGenerateDialog(true);
                          }}
                          className="text-xs text-[#9C0F17] hover:underline"
                        >
                          Liquidar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="distribuidores" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Distribuidores
            </TabsTrigger>
            <TabsTrigger value="prescriptores" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Prescriptores
            </TabsTrigger>
          </TabsList>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600">{stats.borrador}</p>
                  <p className="text-sm text-gray-500">Borrador</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{stats.enviada}</p>
                  <p className="text-sm text-gray-500">Enviadas</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{stats.aceptada}</p>
                  <p className="text-sm text-gray-500">Aceptadas</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{stats.rechazada}</p>
                  <p className="text-sm text-gray-500">Rechazadas</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#9C0F17]">{stats.totalImporte.toFixed(2)}€</p>
                  <p className="text-sm text-gray-500">Importe Total</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="w-[180px]">
                  <Select value={filterPeriodo || 'all'} onValueChange={(v) => setFilterPeriodo(v === 'all' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Periodo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los periodos</SelectItem>
                      {periodOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[200px]">
                  <Select value={filterPartner} onValueChange={setFilterPartner}>
                    <SelectTrigger>
                      <SelectValue placeholder="Partner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los partners</SelectItem>
                      {partners.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.nombre_comercial}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[150px]">
                  <Select value={filterEstado} onValueChange={setFilterEstado}>
                    <SelectTrigger>
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="borrador">Borrador</SelectItem>
                      <SelectItem value="enviada">Enviada</SelectItem>
                      <SelectItem value="validada">Aceptada</SelectItem>
                      <SelectItem value="rechazada">Rechazada</SelectItem>
                      <SelectItem value="facturada">Facturada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content for both tabs */}
          <TabsContent value="distribuidores" className="mt-0">
            <SettlementsList 
              settlements={settlements}
              loading={loading}
              onView={openDetailDialog}
              onSend={handleSend}
              onResend={handleResend}
              onValidate={openValidateDialog}
              onRegenerate={handleRegenerate}
              tipo="distribuidor"
            />
          </TabsContent>
          
          <TabsContent value="prescriptores" className="mt-0">
            <SettlementsList 
              settlements={settlements}
              loading={loading}
              onView={openDetailDialog}
              onSend={handleSend}
              onResend={handleResend}
              onValidate={openValidateDialog}
              onRegenerate={handleRegenerate}
              tipo="prescriptor"
            />
          </TabsContent>
        </Tabs>

        {/* Generate Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Generar Nueva Liquidación</DialogTitle>
              <DialogDescription>
                Selecciona un partner y el periodo para generar la liquidación mensual.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Partner</Label>
                <Select 
                  value={generateForm.partner_id} 
                  onValueChange={(v) => setGenerateForm({...generateForm, partner_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar partner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.filter(p => p.clientes_activos > 0).map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nombre_comercial} ({p.clientes_activos} clientes)
                      </SelectItem>
                    ))}
                    {partners.filter(p => p.clientes_activos === 0).length > 0 && (
                      <>
                        <SelectItem value="" disabled className="text-gray-400">
                          ─ Sin clientes ─
                        </SelectItem>
                        {partners.filter(p => p.clientes_activos === 0).map(p => (
                          <SelectItem key={p.id} value={p.id} disabled>
                            {p.nombre_comercial} (sin clientes)
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Periodo</Label>
                <Select 
                  value={generateForm.periodo} 
                  onValueChange={(v) => setGenerateForm({...generateForm, periodo: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar mes..." />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {generateForm.partner_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800">
                    {activeTab === 'distribuidores' 
                      ? 'Se calculará el precio partner de los productos activos de sus clientes.'
                      : 'Se calcularán las comisiones sobre facturas pagadas de sus clientes.'}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGenerate} 
                className="bg-[#9C0F17]"
                disabled={processing || !generateForm.partner_id || !generateForm.periodo}
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    Generar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <FileText className="w-5 h-5" />
                Liquidación {selectedSettlement?.partner_name} - {selectedSettlement?.periodo}
              </DialogTitle>
            </DialogHeader>
            
            {selectedSettlement && (
              <div className="space-y-6">
                {/* Header info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Partner</p>
                    <p className="font-medium">{selectedSettlement.partner_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Periodo</p>
                    <p className="font-medium">{selectedSettlement.periodo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Estado</p>
                    <Badge className={STATUS_COLORS[selectedSettlement.estado]}>
                      {STATUS_LABELS[selectedSettlement.estado]}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Generada</p>
                    <p className="font-medium">
                      {new Date(selectedSettlement.generada_en).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Rejection reason if exists */}
                {selectedSettlement.motivo_rechazo && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="font-medium text-red-800 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Motivo de rechazo:
                    </p>
                    <p className="text-red-700 mt-1">{selectedSettlement.motivo_rechazo}</p>
                  </div>
                )}

                {/* Lines table */}
                <div>
                  <h3 className="font-medium mb-3">Detalle de Líneas ({settlementLines.length})</h3>
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Concepto</TableHead>
                          <TableHead className="text-right">Precio Orig.</TableHead>
                          <TableHead className="text-right">Días</TableHead>
                          <TableHead className="text-right">Base Prorrat.</TableHead>
                          {selectedSettlement.tipo_partner === 'distribuidor' ? (
                            <>
                              <TableHead className="text-right">Dto.</TableHead>
                              <TableHead className="text-right">Precio Partner</TableHead>
                            </>
                          ) : (
                            <>
                              <TableHead className="text-right">% Com.</TableHead>
                              <TableHead className="text-right">Comisión</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {settlementLines.map((line, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{line.client_name}</TableCell>
                            <TableCell>{line.concepto}</TableCell>
                            <TableCell className="text-right text-gray-500">{line.precio_original?.toFixed(2)}€</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {line.dias_activos}/{line.dias_mes}
                            </TableCell>
                            <TableCell className="text-right">{line.base_calculo_sin_iva?.toFixed(2)}€</TableCell>
                            {selectedSettlement.tipo_partner === 'distribuidor' ? (
                              <>
                                <TableCell className="text-right">{line.descuento_aplicado}%</TableCell>
                                <TableCell className="text-right font-medium text-green-700">
                                  {line.precio_partner?.toFixed(2)}€
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell className="text-right">{line.porcentaje_comision}%</TableCell>
                                <TableCell className="text-right font-medium text-green-700">
                                  {line.importe_comision?.toFixed(2)}€
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center text-lg">
                    <div className="space-y-1">
                      <div className="flex justify-between gap-8">
                        <span className="text-gray-600">Base imponible:</span>
                        <span className="font-medium">{selectedSettlement.total_base?.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between gap-8">
                        <span className="text-gray-600">IVA (21%):</span>
                        <span className="font-medium">{selectedSettlement.total_iva?.toFixed(2)}€</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500 text-sm">TOTAL</p>
                      <p className="text-3xl font-bold text-[#9C0F17]">{selectedSettlement.total?.toFixed(2)}€</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 justify-end pt-4 border-t">
                  {selectedSettlement.estado === 'borrador' && (
                    <>
                      <Button variant="outline" onClick={() => handleRegenerate(selectedSettlement.id)}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerar
                      </Button>
                      <Button className="bg-blue-600" onClick={() => handleSend(selectedSettlement.id)}>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar al Partner
                      </Button>
                    </>
                  )}
                  {selectedSettlement.estado === 'enviada' && (
                    <Button className="bg-green-600" onClick={() => openValidateDialog(selectedSettlement)}>
                      <Check className="w-4 h-4 mr-2" />
                      Validar
                    </Button>
                  )}
                  {selectedSettlement.estado === 'validada' && (
                    <Button 
                      className="bg-purple-600" 
                      onClick={() => handleEmitInvoice(selectedSettlement.id, selectedSettlement.tipo_partner)}
                      disabled={processing}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {selectedSettlement.tipo_partner === 'distribuidor' 
                        ? 'Emitir Factura' 
                        : 'Generar Factura y Pagar'}
                    </Button>
                  )}
                  {selectedSettlement.estado === 'rechazada' && (
                    <Button variant="outline" onClick={() => handleRegenerate(selectedSettlement.id)}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerar
                    </Button>
                  )}
                  {selectedSettlement.estado === 'facturada' && (
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">
                        {selectedSettlement.tipo_partner === 'distribuidor' 
                          ? 'Factura emitida' 
                          : `Factura ${selectedSettlement.numero_factura_prescriptor || ''} generada`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Validate Dialog */}
        <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Validar Liquidación</DialogTitle>
              <DialogDescription>
                Esta acción simula la validación del partner. En producción, el partner validará desde su portal.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex gap-4">
                <Button 
                  variant={validateForm.accepted ? 'default' : 'outline'}
                  className={validateForm.accepted ? 'bg-green-600 flex-1' : 'flex-1'}
                  onClick={() => setValidateForm({...validateForm, accepted: true})}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Aceptar
                </Button>
                <Button 
                  variant={!validateForm.accepted ? 'default' : 'outline'}
                  className={!validateForm.accepted ? 'bg-red-600 flex-1' : 'flex-1'}
                  onClick={() => setValidateForm({...validateForm, accepted: false})}
                >
                  <X className="w-4 h-4 mr-2" />
                  Rechazar
                </Button>
              </div>
              
              {!validateForm.accepted && (
                <div>
                  <Label>Motivo del rechazo</Label>
                  <Textarea
                    value={validateForm.motivo_rechazo}
                    onChange={(e) => setValidateForm({...validateForm, motivo_rechazo: e.target.value})}
                    placeholder="Indica el motivo del rechazo..."
                    rows={3}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowValidateDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleValidate}
                className={validateForm.accepted ? 'bg-green-600' : 'bg-red-600'}
                disabled={processing || (!validateForm.accepted && !validateForm.motivo_rechazo)}
              >
                {processing ? 'Procesando...' : 'Confirmar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// Settlements List Component
function SettlementsList({ settlements, loading, onView, onSend, onResend, onValidate, onRegenerate, tipo }) {
  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
        <p className="text-gray-500 mt-2">Cargando liquidaciones...</p>
      </div>
    );
  }

  if (settlements.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No hay liquidaciones para mostrar</p>
        <p className="text-sm mt-1">
          {tipo === 'distribuidor' 
            ? 'Genera una liquidación seleccionando un distribuidor con clientes asignados'
            : 'Genera una liquidación seleccionando un prescriptor con clientes asignados'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-4">
      {settlements.map(settlement => (
        <Card key={settlement.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-gray-100 rounded-lg p-3">
                  <Calendar className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-medium">{settlement.partner_name}</h3>
                  <p className="text-sm text-gray-500">
                    {MONTHS[parseInt(settlement.periodo.split('-')[1]) - 1]} {settlement.periodo.split('-')[0]}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <Badge className={STATUS_COLORS[settlement.estado]}>
                  {STATUS_LABELS[settlement.estado]}
                </Badge>
                
                <div className="text-right">
                  <p className="text-lg font-bold">{settlement.total?.toFixed(2)}€</p>
                  <p className="text-xs text-gray-500">Base: {settlement.total_base?.toFixed(2)}€</p>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => onView(settlement)}>
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </Button>
                  
                  {settlement.estado === 'borrador' && (
                    <Button size="sm" className="bg-blue-600" onClick={() => onSend(settlement.id)}>
                      <Send className="w-4 h-4 mr-1" />
                      Enviar
                    </Button>
                  )}

                  {/* Ya enviada: se puede volver a mandar. Pasa constantemente
                      que el partner lo pierde o se le va a spam. No cambia el
                      estado: reenviar una validada no puede des-validarla. */}
                  {settlement.estado !== 'borrador' && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => onResend(settlement)}
                      title={settlement.enviada_a
                        ? `Volver a enviar a ${settlement.enviada_a}`
                        : 'Volver a enviar al partner'}
                      data-testid={`resend-${settlement.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Reenviar
                      {settlement.reenvios > 0 && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({settlement.reenvios})
                        </span>
                      )}
                    </Button>
                  )}

                  {settlement.estado === 'enviada' && (
                    <Button size="sm" className="bg-green-600" onClick={() => onValidate(settlement)}>
                      <Check className="w-4 h-4 mr-1" />
                      Validar
                    </Button>
                  )}
                  
                  {settlement.estado === 'rechazada' && (
                    <Button variant="outline" size="sm" onClick={() => onRegenerate(settlement.id)}>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
